/* ==================================================
   VAULT.JS — VaultLab Motion/UX Layer

   - Scroll reveals via IntersectionObserver
   - Scroll progress → CSS var --vl-progress on sections
   - Sticky header scroll state (html.vl-scrolled)
   - Light tilt hover (no dependencies)
   - Product: variant selection sync (works with hidden input)
   - Product recommendations: AJAX hydrate (optional)

   Respects:
   - html[data-vl-motion="off"]
   - prefers-reduced-motion
   ================================================== */

(function() {
  'use strict';

  // Expose a tiny marker for legacy shims / debugging.
  window.__VL_VAULT_JS_LOADED = true;

  const html = document.documentElement;
  const reduceMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverFinePointerMql = window.matchMedia('(hover: hover) and (pointer: fine)');

  const state = {
    get motionAllowed() {
      return html.dataset.vlMotion !== 'off' && !reduceMotionMql.matches;
    },
    raf: 0,
    scrollY: 0,
  };

  function clamp01(n) {
    return Math.min(1, Math.max(0, n));
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
      return;
    }
    fn();
  }

  /* ---------------------------------------------
     1) Header scroll state + scroll padding
     --------------------------------------------- */
  function initHeader() {
    const header = document.querySelector('header.vl-header, header');
    if (!header) return;

    // Keep scroll padding in sync with header height (anchors land correctly)
    const setScrollPadding = () => {
      const height = Math.round(header.getBoundingClientRect().height);
      html.style.setProperty('--vl-scroll-padding', `${Math.max(56, height) + 16}px`);
    };

      /* ---------------------------------------------
     X) Header search overlay
     --------------------------------------------- */
  function initSearchOverlay() {
    const searchToggle = document.querySelector('.header__search-toggle');
    const searchOverlay = document.querySelector('[data-search-overlay]');
    const searchClose = document.querySelector('[data-search-close]');
    const searchInput =
      searchOverlay?.querySelector('.search-overlay__input') ||
      searchOverlay?.querySelector('input[type="search"], input[name="q"]');

    if (!searchToggle || !searchOverlay) return;
    if (searchToggle.dataset.vlSearchBound === '1') return;
    searchToggle.dataset.vlSearchBound = '1';

    let lastFocused = null;

    const open = () => {
      lastFocused = document.activeElement;
      searchOverlay.classList.add('active');
      html.classList.add('vl-search-open');

      searchToggle.setAttribute('aria-expanded', 'true');
      searchOverlay.setAttribute('aria-hidden', 'false');

      // focus input
      window.setTimeout(() => searchInput?.focus(), 0);
    };

    const close = () => {
      searchOverlay.classList.remove('active');
      html.classList.remove('vl-search-open');

      searchToggle.setAttribute('aria-expanded', 'false');
      searchOverlay.setAttribute('aria-hidden', 'true');

      // return focus
      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }
    };

    searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      open();
    });

    searchClose?.addEventListener('click', (e) => {
      e.preventDefault();
      close();
    });

    // click outside content closes
    searchOverlay.addEventListener('click', (e) => {
      if (e.target === searchOverlay) close();
    });

    // ESC closes
    document.addEventListener('keydown', (e) => {
      if (!searchOverlay.classList.contains('active')) return;
      if (e.key === 'Escape') close();
    });
  }


    setScrollPadding();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(setScrollPadding);
      ro.observe(header);
    } else {
      window.addEventListener('resize', setScrollPadding, { passive: true });
    }

    const toggleScrolled = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      html.classList.toggle('vl-scrolled', y > 16);
    };

    toggleScrolled();
    window.addEventListener('scroll', toggleScrolled, { passive: true });
  }

  /* ---------------------------------------------
     2) Intersection reveals
     --------------------------------------------- */
  function initReveals() {
    const nodes = Array.from(document.querySelectorAll('[data-reveal]'))
      .filter((el) => !el.classList.contains('is-visible') && el.dataset.vlRevealBound !== '1');
    if (!nodes.length) return;

    // If motion is disabled, ensure visible immediately.
    if (!state.motionAllowed) {
      nodes.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        });
      },
      { root: null, threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
    );

    nodes.forEach((el) => {
      el.dataset.vlRevealBound = '1';
      io.observe(el);
    });
  }

  /* ---------------------------------------------
     3) Scroll progress (hero fade/parallax)
     --------------------------------------------- */
  function initScrollProgress() {
    const sections = Array.from(document.querySelectorAll('[data-vl-scroll-section]'));
    if (!sections.length) return;

    if (!state.motionAllowed) {
      sections.forEach((el) => el.style.setProperty('--vl-progress', '0'));
      return;
    }

    const update = () => {
      state.raf = 0;
      const vh = Math.max(1, window.innerHeight || 1);
      for (const el of sections) {
        const rect = el.getBoundingClientRect();
        const total = vh + rect.height;
        const progressed = (vh - rect.top) / total;
        const p = clamp01(progressed);
        el.style.setProperty('--vl-progress', p.toFixed(4));
      }
    };

    const request = () => {
      if (state.raf) return;
      state.raf = window.requestAnimationFrame(update);
    };

    request();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
  }

  /* ---------------------------------------------
     4) Tilt hover (micro interaction)
     --------------------------------------------- */
  function initTilt() {
    const items = Array.from(document.querySelectorAll('[data-vl-tilt]'));
    if (!items.length) return;
    if (!state.motionAllowed) return;
    if (!hoverFinePointerMql.matches) return;

    const maxDegDefault = 6;
    const zLift = 10;

    items.forEach((el) => {
      if (el.dataset.vlTiltBound === '1') return;
      el.dataset.vlTiltBound = '1';
      let rect = null;

      const readRect = () => {
        rect = el.getBoundingClientRect();
      };

      const onEnter = () => {
        readRect();
        el.style.setProperty('--vl-tilt-transition', '0.18s');
        el.style.setProperty('--vl-tilt-z', `${zLift}px`);
      };

      const onMove = (e) => {
        if (!rect) readRect();
        const max = Number(el.getAttribute('data-vl-tilt')) || maxDegDefault;

        const x = (e.clientX - rect.left) / Math.max(1, rect.width);
        const y = (e.clientY - rect.top) / Math.max(1, rect.height);

        const ry = (x - 0.5) * (max * 2);
        const rx = (0.5 - y) * (max * 2);

        el.style.setProperty('--vl-tilt-x', `${rx.toFixed(2)}deg`);
        el.style.setProperty('--vl-tilt-y', `${ry.toFixed(2)}deg`);
        el.style.setProperty('--vl-tilt-transition', '0s');
      };

      const onLeave = () => {
        el.style.setProperty('--vl-tilt-transition', '0.35s');
        el.style.setProperty('--vl-tilt-x', '0deg');
        el.style.setProperty('--vl-tilt-y', '0deg');
        el.style.setProperty('--vl-tilt-z', '0px');
        rect = null;
      };

      el.addEventListener('pointerenter', onEnter);
      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
    });
  }

  /* ---------------------------------------------
     5) Anchor smooth scroll with header offset
     --------------------------------------------- */
  function initAnchorScroll() {
    const header = document.querySelector('header.vl-header, header');
    const getOffset = () => (header ? Math.round(header.getBoundingClientRect().height) + 16 : 80);

    document.addEventListener('click', (e) => {
      const link = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href === '#' || href.length < 2) return;

      const id = href.slice(1);
      const target = document.getElementById(id);
      if (!target) return;

      e.preventDefault();

      const y = window.scrollY + target.getBoundingClientRect().top - getOffset();

      // Respect reduced motion: jump directly.
      if (!state.motionAllowed) {
        window.scrollTo(0, y);
        target.focus({ preventScroll: true });
        return;
      }

      window.scrollTo({ top: y, left: 0, behavior: 'smooth' });
      // Focus after scroll settles a bit.
      window.setTimeout(() => target.focus({ preventScroll: true }), 350);
    });
  }

  /* ---------------------------------------------
     6) Product — variant selection sync
     --------------------------------------------- */
  function formatMoney(cents, format) {
    const value = typeof cents === 'string' ? cents : String(cents);
    const amount = parseInt(value, 10);
    if (!Number.isFinite(amount)) return '';

    // Shopify money format tokens: {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}}, etc.
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = format || (window.VL && window.VL.moneyFormat) || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      const num = (number / 100).toFixed(precision);
      const parts = num.split('.');
      const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    const match = fmt.match(placeholderRegex);
    if (!match) return fmt;

    const token = match[1];
    let formatted = '';

    switch (token) {
      case 'amount':
        formatted = formatWithDelimiters(amount, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(amount, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(amount, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        formatted = formatWithDelimiters(amount, 0, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(amount, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(amount, 2, ',', '.');
        break;
    }

    return fmt.replace(placeholderRegex, formatted);
  }

  function initVariantSelection() {
    const forms = Array.from(document.querySelectorAll('form[data-product-form]'));
    if (!forms.length) return;

    const variants = Array.isArray(window.productVariants) ? window.productVariants : null;
    if (!variants || !variants.length) return;

    forms.forEach((form) => {
      const hiddenId = form.querySelector('input[name="id"]');
      if (!hiddenId) return;

      const optionButtons = Array.from(form.querySelectorAll('[data-variant-option]'));
      if (!optionButtons.length) return;

      const priceEl = document.querySelector('.product-info__price');
      const addBtn = form.querySelector('button[name="add"]');
      const selectedValueEls = Array.from(form.querySelectorAll('[data-selected-value]'));
      const mainImage = document.querySelector('[data-product-gallery] [data-main-image]');

      function getSelectedOptionsInOrder() {
        // Build a stable order from the data-selected-value nodes (they follow Liquid option order)
        const optionNames = selectedValueEls.map((el) => el.getAttribute('data-selected-value'));
        const selectedByName = new Map();
        form.querySelectorAll('[data-variant-option].active').forEach((btn) => {
          selectedByName.set(btn.dataset.optionName, btn.dataset.optionValue);
        });

        // Fallback: if nothing is active yet, use current variant options.
        if (selectedByName.size === 0) {
          const current = variants.find((v) => String(v.id) === String(hiddenId.value));
          if (current && Array.isArray(current.options)) return current.options;
        }

        return optionNames.map((name) => selectedByName.get(name)).filter(Boolean);
      }

      function findVariant(selectedOptions) {
        return variants.find((v) => {
          if (!Array.isArray(v.options)) {
            // Older shape: option1/option2/option3
            const opts = [v.option1, v.option2, v.option3].filter(Boolean);
            return opts.join('||') === selectedOptions.join('||');
          }
          return v.options.join('||') === selectedOptions.join('||');
        });
      }

      function applyVariant(variant) {
        if (!variant) return;
        hiddenId.value = variant.id;

        // Price
        if (priceEl && typeof variant.price !== 'undefined') {
          priceEl.textContent = formatMoney(variant.price);
        }

        // Availability
        if (addBtn) {
          if (variant.available) {
            addBtn.removeAttribute('disabled');
            addBtn.textContent = 'ADD TO CART';
          } else {
            addBtn.setAttribute('disabled', 'disabled');
            addBtn.textContent = 'SOLD OUT';
          }
        }

        // URL (shareable variant)
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', url.toString());
        } catch (_) {
          // ignore
        }

        // Main image swap if variant has featured_image
        if (mainImage && variant.featured_image && variant.featured_image.src) {
          mainImage.src = variant.featured_image.src;
          mainImage.removeAttribute('srcset');
        }
      }

      optionButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const optionName = btn.dataset.optionName;
          const optionValue = btn.dataset.optionValue;

          // Active state within same option group
          form.querySelectorAll(`[data-option-name="${CSS.escape(optionName)}"]`).forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');

          // Update the "selected value" label next to option name
          const label = selectedValueEls.find((el) => el.getAttribute('data-selected-value') === optionName);
          if (label) label.textContent = optionValue;

          const selectedOptions = getSelectedOptionsInOrder();
          const variant = findVariant(selectedOptions);
          applyVariant(variant);
        });

        // Keyboard support
        btn.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            btn.click();
          }
        });
      });
    });
  }

  /* ---------------------------------------------
     7) Product recommendations (AJAX hydrate)
     --------------------------------------------- */
  function initProductRecommendations() {
    const containers = Array.from(document.querySelectorAll('[data-product-recommendations][data-url]'));
    if (!containers.length) return;

    containers.forEach(async (container) => {
      const url = container.getAttribute('data-url');
      if (!url) return;

      try {
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return;
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-product-recommendations]');
        if (!fresh) return;
        container.innerHTML = fresh.innerHTML;
        // Remove current product from injected recommendations (prevents duplicate)
try {
  const currentHandle =
    document.querySelector('link[rel="canonical"]')?.href?.split('/products/')[1]?.split(/[?#]/)[0] || null;

  if (currentHandle) {
    container.querySelectorAll('a[href*="/products/"]').forEach((a) => {
      const href = a.getAttribute('href') || '';
      const handle = href.split('/products/')[1]?.split(/[?#]/)[0];
      if (handle && handle === currentHandle) {
        a.closest('.product-card')?.remove();
      }
    });
  }

  // If after removal you have more than 2 items (you have only 3 products), keep max 2
  const cards = Array.from(container.querySelectorAll('.product-card'));
  const maxShow = 2;
  cards.slice(maxShow).forEach((el) => el.remove());
} catch (e) {
  // ignore
}


        // Re-bind motion behaviors for newly injected content.
        initReveals();
        initTilt();
      } catch (_) {
        // silent fail (fallback content stays)
      }
    });
  }


    function initSearchOverlay() {
  const searchToggle = document.querySelector('.header__search-toggle');
  const searchOverlay = document.querySelector('[data-search-overlay]');
  const searchClose = document.querySelector('[data-search-close]');
  const searchInput =
    searchOverlay?.querySelector('.search-overlay__input') ||
    searchOverlay?.querySelector('input[type="search"], input[name="q"]');

  if (!searchToggle || !searchOverlay) return;
  if (searchToggle.dataset.vlSearchBound === '1') return;
  searchToggle.dataset.vlSearchBound = '1';

  let lastFocused = null;

  const open = () => {
    lastFocused = document.activeElement;
    searchOverlay.classList.add('active');
    document.documentElement.classList.add('vl-search-open');

    searchToggle.setAttribute('aria-expanded', 'true');
    searchOverlay.setAttribute('aria-hidden', 'false');

    window.setTimeout(() => searchInput?.focus(), 0);
  };

  const close = () => {
    searchOverlay.classList.remove('active');
    document.documentElement.classList.remove('vl-search-open');

    searchToggle.setAttribute('aria-expanded', 'false');
    searchOverlay.setAttribute('aria-hidden', 'true');

    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  };

  searchToggle.addEventListener('click', (e) => {
    e.preventDefault();
    open();
  });

  searchClose?.addEventListener('click', (e) => {
    e.preventDefault();
    close();
  });

  searchOverlay.addEventListener('click', (e) => {
    if (e.target === searchOverlay) close();
  });

  document.addEventListener('keydown', (e) => {
    if (!searchOverlay.classList.contains('active')) return;
    if (e.key === 'Escape') close();
  });
}

  /* ---------------------------------------------
     Boot
     --------------------------------------------- */
  onReady(() => {
    initHeader();
    initSearchOverlay();
    initAnchorScroll();
    initReveals();
    initScrollProgress();
    initTilt();
    initVariantSelection();
    initProductRecommendations();
  });

})();
